package kz.dentalcrm.app.screens.appointments

import com.kizitonwose.calendar.core.now
import dentalcrm.composeapp.generated.resources.Res
import dentalcrm.composeapp.generated.resources.error_message
import dentalcrm.composeapp.generated.resources.snackbar_appointment_deleted
import kotlinx.collections.immutable.toPersistentList
import kotlinx.collections.immutable.toPersistentMap
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate
import kz.dentalcrm.app.core.base.BaseStateViewModel
import kz.dentalcrm.app.core.ui.ScreenState
import kz.dentalcrm.app.core.utils.parseServerDateTimeToMillis
import kz.dentalcrm.app.core.utils.stateHandle
import kz.dentalcrm.app.data.network.request_model.AppointmentRequest
import kz.dentalcrm.app.data.network.response_model.AppointmentResponse
import kz.dentalcrm.app.data.network.response_model.employees.Employee
import kz.dentalcrm.app.domain.AppointmentRepository
import kz.dentalcrm.app.screens.appointments.mvi.AppointmentViewEvent
import kz.dentalcrm.app.screens.appointments.mvi.AppointmentsViewSideEffect
import kz.dentalcrm.app.screens.appointments.mvi.AppointmentsViewState
import kz.dentalcrm.app.screens.appointments.mvi.models.UIAppointment
import kz.dentalcrm.app.theme.SnackbarController
import kz.dentalcrm.app.theme.SnackbarEvent
import org.jetbrains.compose.resources.getString
import org.koin.core.component.inject
import kotlin.time.ExperimentalTime

@OptIn(ExperimentalTime::class)
class AppointmentsViewModel : BaseStateViewModel<AppointmentViewEvent,
        AppointmentsViewState,
        AppointmentsViewSideEffect>() {

    private val appointmentRepository: AppointmentRepository by inject()

    fun getAppointments(
        dateFrom: LocalDate,
        dateTo: LocalDate,
        employeeId: String? = null,
    ) {
        setState { copy(screenState = ScreenState.Loading) }
        launch(Dispatchers.Default) {
            appointmentRepository.getAppointments(
                dateFrom = dateFrom,
                dateTo = dateTo,
                employeeId = employeeId
            ).stateHandle(
                onError = { _, _ ->
                    setState { copy(screenState = ScreenState.Idle) }
                },
                onFailure = {
                    setState { copy(screenState = ScreenState.Idle) }
                },
                onSuccess = { data ->
                    val uiAppointments = data.map(::mapToUI)
                    val grouped = uiAppointments.groupBy { it.data.employee }
                    val validGrouped = mutableMapOf<Employee, List<UIAppointment>>()
                    for ((key, value) in grouped) {
                        if (key != null) {
                            validGrouped[key] = value
                        }
                    }
                    val employeesList = validGrouped.keys.toList()
                    val persistentGrouped = validGrouped.mapValues { entry -> 
                        entry.value.sortedBy { app -> app.millis }.toPersistentList() 
                    }.toPersistentMap()

                    val allAppointmentsList = uiAppointments.sortedBy { it.millis }.toPersistentList()

                    setState {
                        copy(
                            dateFrom = dateFrom,
                            dateTo = dateTo,
                            selectedDate = dateFrom,
                            selectedEmployee = if (viewState.value.selectedEmployee == null) {
                                persistentGrouped.keys.firstOrNull()
                            } else {
                                viewState.value.selectedEmployee
                            },
                            screenState = ScreenState.Idle,
                            appointmentsByEmployee = persistentGrouped,
                            allAppointments = allAppointmentsList,
                            employees = employeesList.toPersistentList()
                        )
                    }
                }
            )
        }
    }

    @OptIn(ExperimentalTime::class)
    fun deleteAppointment(id: Int) {
        launch {
            appointmentRepository.deleteAppointment(id = id).stateHandle(
                onError = { _, _ ->
                    SnackbarController.sendEvent(SnackbarEvent(getString(Res.string.error_message), SnackbarEvent.SnackbarType.Red))
                },
                onFailure = {
                    SnackbarController.sendEvent(SnackbarEvent(getString(Res.string.error_message), SnackbarEvent.SnackbarType.Red))
                },
                onSuccess = {
                    SnackbarController.sendEvent(SnackbarEvent(getString(Res.string.snackbar_appointment_deleted), SnackbarEvent.SnackbarType.Green))
                    getAppointments(
                        dateFrom = viewState.value.dateFrom ?: LocalDate.now(),
                        dateTo = viewState.value.dateTo ?: LocalDate.now(),
                    )
                }
            )
        }
    }

    private fun updateAppointmentStatus(appointmentId: Int, status: String) {
        setState { copy(screenState = ScreenState.Loading) }
        launch {
            // Fetch cashboxes to get main one as fallback
            val cashBoxesResult = appointmentRepository.getCashBoxes()
            val mainCashBoxId = if (cashBoxesResult is kz.dentalcrm.app.core.utils.Result.Success) {
                cashBoxesResult.value.firstOrNull { it.isMain }?.id
            } else {
                null
            }

            appointmentRepository.getAppointment(appointmentId).stateHandle(
                onError = { _, _ ->
                    setState { copy(screenState = ScreenState.Idle) }
                },
                onFailure = {
                    setState { copy(screenState = ScreenState.Idle) }
                },
                onSuccess = { appointment ->
                    appointment?.let {
                        val request = AppointmentRequest(
                            cash_box_id = it.cash_box_id ?: mainCashBoxId,
                            employee = it.employee,
                            ends_at = it.ends_at,
                            is_first_visit = it.is_first_visit,
                            patient = it.patient,
                            price = it.price,
                            services = emptyList(),
                            starts_at = it.starts_at,
                            title = it.title,
                            status = status,
                            comment = it.comment,
                            treatment_course = it.treatment_course
                        )
                        appointmentRepository.updateAppointment(appointmentId, request).stateHandle(
                            onError = { _, _ ->
                                setState { copy(screenState = ScreenState.Idle) }
                            },
                            onFailure = {
                                setState { copy(screenState = ScreenState.Idle) }
                            },
                            onSuccess = {
                                getAppointments(
                                    dateFrom = viewState.value.dateFrom ?: LocalDate.now(),
                                    dateTo = viewState.value.dateTo ?: LocalDate.now(),
                                )
                            }
                        )
                    } ?: run {
                        setState { copy(screenState = ScreenState.Idle) }
                    }
                }
            )
        }
    }

    fun mapToUI(data: AppointmentResponse): UIAppointment {
        return UIAppointment(
            data = data,
            millis = data.starts_at?.parseServerDateTimeToMillis() ?: 0,
            time = data.starts_at?.formatTime() ?: ""
        )
    }

    fun String.formatTime(): String = runCatching {
        val times = this.split(" ")
            .last()
            .split(":")
        "${times[0]}:${times[1]}"
    }.getOrNull().orEmpty()

    override fun setInitialState() = AppointmentsViewState()

    override fun handleEvents(event: AppointmentViewEvent) {
        when (event) {
            is AppointmentViewEvent.LoadData -> getAppointments(
                dateFrom = event.dateFrom,
                dateTo = event.dateTo,
                employeeId = event.employeeId
            )

            is AppointmentViewEvent.ReloadData -> getAppointments(
                dateFrom = viewState.value.dateFrom ?: LocalDate.now(),
                dateTo = viewState.value.dateTo ?: LocalDate.now(),
            )

            is AppointmentViewEvent.SelectEmployee -> {
                setState {
                    viewState.value.copy(
                        selectedEmployee = event.employee
                    )
                }
            }

            is AppointmentViewEvent.OnStatusChanged -> updateAppointmentStatus(
                appointmentId = event.appointmentId,
                status = event.status
            )

            is AppointmentViewEvent.Delete -> deleteAppointment(id = event.appointmentId)
            is AppointmentViewEvent.Edit -> setEffect { AppointmentsViewSideEffect.NavigateToEdit(event.appointmentId) }
        }
    }


}